import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Loader2, BookOpenCheck, ChevronDown } from "lucide-react";
import { toast } from "sonner";

type DetailedBlueprintData = {
  audience_segments?: { name: string; description: string; pain_points: string[] }[];
  content_strategy?: { channel: string; format: string; cadence: string; hook: string }[];
  pricing_models?: { tier: string; price: string; includes: string[] }[];
  launch_checklist?: { phase: string; items: string[] }[];
  generated_at?: string;
};

type Props = {
  trendId: string;
  initial?: DetailedBlueprintData;
  canRegenerate?: boolean; // admins only
};

export function DetailedBlueprint({ trendId, initial, canRegenerate = false }: Props) {
  const [data, setData] = useState<DetailedBlueprintData | undefined>(initial);
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(true);

  const generate = async () => {
    setBusy(true);
    const { data: res, error } = await supabase.functions.invoke("generate-detailed-blueprint", {
      body: { trend_id: trendId },
    });
    setBusy(false);
    if (error) {
      const msg = (res as { error?: string } | null)?.error ?? error.message;
      return toast.error(msg);
    }
    const next = (res as { detailed?: DetailedBlueprintData })?.detailed;
    if (!next) return toast.error("Could not generate detailed blueprint");
    setData(next);
    toast.success("Detailed blueprint ready");
  };

  const hasData =
    data &&
    (data.audience_segments?.length ||
      data.content_strategy?.length ||
      data.pricing_models?.length ||
      data.launch_checklist?.length);

  return (
    <div className="mt-3 rounded-lg border border-border/60 bg-secondary/20 p-3 text-sm">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="flex items-center gap-2 font-mono text-[10px] uppercase text-muted-foreground hover:text-foreground"
        >
          <BookOpenCheck className="size-3.5" />
          detailed blueprint
          <ChevronDown className={`size-3 transition-transform ${open ? "rotate-180" : ""}`} />
        </button>
        {canRegenerate && (
          <Button size="sm" variant="outline" onClick={generate} disabled={busy}>
            {busy ? <Loader2 className="size-3.5 animate-spin" /> : <BookOpenCheck className="size-3.5" />}
            {hasData ? "Regenerate" : "Generate detailed"}
          </Button>
        )}
      </div>

      {open && (
        <div className="mt-3 space-y-4">
          {!hasData ? (
            <p className="text-xs text-muted-foreground">
              {canRegenerate
                ? "No detailed blueprint yet. Generate one to get audience segments, content strategy, pricing tiers, and a launch checklist."
                : "Detailed blueprint will appear here once a curator generates it."}
            </p>
          ) : (
            <>
              {data?.audience_segments?.length ? (
                <Section title="Audience segments">
                  <div className="grid sm:grid-cols-2 gap-2">
                    {data.audience_segments.map((s, i) => (
                      <div key={i} className="rounded border border-border bg-background p-2.5">
                        <div className="font-semibold text-xs">{s.name}</div>
                        <div className="text-[11px] text-muted-foreground mt-0.5">{s.description}</div>
                        {s.pain_points?.length > 0 && (
                          <ul className="mt-1.5 list-disc list-inside text-[11px] text-muted-foreground">
                            {s.pain_points.map((p, j) => <li key={j}>{p}</li>)}
                          </ul>
                        )}
                      </div>
                    ))}
                  </div>
                </Section>
              ) : null}

              {data?.content_strategy?.length ? (
                <Section title="Content strategy">
                  <div className="space-y-1.5">
                    {data.content_strategy.map((c, i) => (
                      <div key={i} className="rounded border border-border bg-background p-2.5 text-xs">
                        <div className="flex flex-wrap gap-x-3 gap-y-1">
                          <b>{c.channel}</b>
                          <span className="text-muted-foreground">{c.format}</span>
                          <span className="font-mono text-[10px] text-muted-foreground">{c.cadence}</span>
                        </div>
                        <div className="text-[11px] text-muted-foreground mt-1">Hook: {c.hook}</div>
                      </div>
                    ))}
                  </div>
                </Section>
              ) : null}

              {data?.pricing_models?.length ? (
                <Section title="Pricing tiers">
                  <div className="grid sm:grid-cols-3 gap-2">
                    {data.pricing_models.map((p, i) => (
                      <div key={i} className="rounded border border-border bg-background p-2.5">
                        <div className="font-semibold text-xs">{p.tier}</div>
                        <div className="font-display text-lg text-primary">{p.price}</div>
                        <ul className="mt-1 list-disc list-inside text-[11px] text-muted-foreground">
                          {p.includes?.map((it, j) => <li key={j}>{it}</li>)}
                        </ul>
                      </div>
                    ))}
                  </div>
                </Section>
              ) : null}

              {data?.launch_checklist?.length ? (
                <Section title="Launch checklist">
                  <div className="space-y-2">
                    {data.launch_checklist.map((ph, i) => (
                      <div key={i} className="rounded border border-border bg-background p-2.5">
                        <div className="font-semibold text-xs mb-1">{ph.phase}</div>
                        <ul className="text-[11px] text-muted-foreground space-y-0.5">
                          {ph.items?.map((it, j) => (
                            <li key={j} className="flex gap-1.5">
                              <span className="text-primary">▢</span>
                              <span>{it}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    ))}
                  </div>
                </Section>
              ) : null}
            </>
          )}
        </div>
      )}
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <div className="font-mono text-[10px] uppercase text-primary mb-1.5">{title}</div>
      {children}
    </div>
  );
}

export type { DetailedBlueprintData };
